import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Search, Gift, ChevronRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;
const STATUS_CLS = {
  unused: "bg-green-100 text-green-700", partial: "bg-amber-100 text-amber-700",
  depleted: "bg-slate-200 text-slate-600", refunded: "bg-red-100 text-red-600", withdrawn: "bg-red-100 text-red-600",
};
const SOURCE_LABEL = { direct_purchase: "Sold", on_attend_credit: "On-attend credit", accountant_topup: "Top-up" };

function CouponRow({ c, onOpen }) {
  return (
    <button onClick={() => onOpen(c)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-white border hover:border-[#1e3a5f] text-left transition-colors" data-testid={`coupon-row-${c.code}`}>
      <Gift className="w-5 h-5 text-amber-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-mono font-bold text-[#1e3a5f]">{c.code}</p>
        <p className="text-xs text-slate-500 truncate">{c.issued_to_email || "No email"} · {SOURCE_LABEL[c.source] || c.source} · {new Date(c.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</p>
      </div>
      <div className="text-right shrink-0">
        <p className="font-bold text-sm">{eur(c.balance)}<span className="text-slate-400 font-normal"> / {eur(c.amount)}</span></p>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_CLS[c.status] || ""}`}>{(c.status || "").toUpperCase()}</span>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300" />
    </button>
  );
}

export default function CouponLookup({ eventId, onBack, onOpen, initialQuery = "" }) {
  const { authAxios } = useAuth();
  const [q, setQ] = useState(initialQuery);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await authAxios.get("/coupons", { params: { event_id: eventId, search: q.trim() || undefined } });
        setRows(r.data);
      } catch (e) { toast.error(e.response?.data?.detail || "Could not load coupons"); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="coupon-lookup">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-600 mb-4"><ArrowLeft className="w-5 h-5" /> Back to Event</button>
      <h2 className="font-bold text-lg text-[#1e3a5f] mb-3">Find Coupon</h2>
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Coupon code or buyer email"
          className="w-full pl-9 pr-3 py-3 rounded-xl border text-sm" data-testid="coupon-lookup-input" />
      </div>
      {rows === null ? <p className="text-sm text-slate-400 text-center py-6">Loading…</p>
        : rows.length === 0 ? <p className="text-sm text-slate-400 text-center py-6" data-testid="coupon-lookup-empty">No coupons found for this event.</p>
        : <div className="space-y-2" data-testid="coupon-lookup-results">{rows.map(c => <CouponRow key={c.id} c={c} onOpen={onOpen} />)}</div>}
    </div>
  );
}
